import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface LogEntry {
  id: string;
  company_id: string;
  user_id: string;
  action: string;
  details: Record<string, unknown> | null;
  ip_address: string;
  created_at: string;
  user_name: string;
}

interface LogsResponse {
  logs: LogEntry[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  actions: string[];
}

interface UserOption {
  id: string;
  full_name: string;
  email: string;
}

const ACTION_COLORS: Record<string, string> = {
  'login': 'bg-secondary-100 text-secondary-700 dark:bg-secondary-500/10 dark:text-secondary-400',
  'logout': 'bg-background-200 text-foreground-600 dark:bg-background-300 dark:text-foreground-400',
  'campaign_started': 'bg-accent-100 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400',
  'campaign_paused': 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  'campaign_completed': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  'template_created': 'bg-primary-100 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400',
  'template_deleted': 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  'contacts_imported': 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400',
  'contact_edited': 'bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400',
  'group_created': 'bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-400',
  'webhook': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400',
  'settings': 'bg-foreground-100 text-foreground-700 dark:bg-foreground-500/10 dark:text-foreground-400',
  'plan_changed': 'bg-lime-100 text-lime-700 dark:bg-lime-500/10 dark:text-lime-400',
  'user_created': 'bg-teal-100 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400',
  'user_deleted': 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
};

const ACTION_ICONS: Record<string, string> = {
  'login': 'ri-login-box-line',
  'logout': 'ri-logout-box-line',
  'campaign_started': 'ri-play-circle-line',
  'campaign_paused': 'ri-pause-circle-line',
  'campaign_completed': 'ri-check-double-line',
  'template_created': 'ri-file-add-line',
  'template_deleted': 'ri-delete-bin-line',
  'contacts_imported': 'ri-upload-cloud-2-line',
  'contact_edited': 'ri-user-settings-line',
  'group_created': 'ri-folder-add-line',
  'webhook': 'ri-plug-line',
  'settings': 'ri-settings-3-line',
  'plan_changed': 'ri-exchange-line',
  'user_created': 'ri-user-add-line',
  'user_deleted': 'ri-user-unfollow-line',
};

function getActionStyle(action: string) {
  const lower = action.toLowerCase().replace(/\s+/g, '_');
  for (const [key, style] of Object.entries(ACTION_COLORS)) {
    if (lower.includes(key)) return style;
  }
  return 'bg-background-200 text-foreground-600 dark:bg-background-300 dark:text-foreground-400';
}

function getActionIcon(action: string) {
  const lower = action.toLowerCase().replace(/\s+/g, '_');
  for (const [key, icon] of Object.entries(ACTION_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return 'ri-information-line';
}

export default function AdminLogsPage() {
  const { profile } = useAuth();
  const [data, setData] = useState<LogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(1);

  // User search
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');

  const perPage = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      if (filterAction) params.set('action', filterAction);
      if (filterUserId) params.set('user_id', filterUserId);
      if (filterDateFrom) params.set('date_from', new Date(filterDateFrom).toISOString());
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        params.set('date_to', toDate.toISOString());
      }

      const { data: result, error: fnError } = await supabase.functions.invoke(
        `admin-logs?${params.toString()}`,
        { method: 'GET' },
      );
      if (fnError) throw new Error(fnError.message);
      if (result?.error) throw new Error(result.error);
      setData(result as LogsResponse);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterUserId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Debounced user search
  useEffect(() => {
    if (userSearch.length < 2) {
      setUserOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .ilike('full_name', `%${userSearch}%`)
          .limit(8);

        if (profiles) {
          const enriched = await Promise.all(
            profiles.map(async (p) => {
              let email = '';
              try {
                const { data: result } = await supabase.functions.invoke('admin-manage-users', { method: 'GET' });
                if (result?.users) {
                  const found = result.users.find((u: UserOption) => u.id === p.id);
                  email = found?.email || '';
                }
              } catch { /* ignore */ }
              return { id: p.id, full_name: p.full_name, email };
            })
          );
          setUserOptions(enriched);
        }
      } catch { /* ignore */ }
      setSearchingUsers(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  if (profile && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const clearFilters = () => {
    setFilterAction('');
    setFilterUserId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSelectedUserName('');
    setUserSearch('');
    setPage(1);
  };

  const hasFilters = filterAction || filterUserId || filterDateFrom || filterDateTo;

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderPageButtons = () => {
    if (!data || data.totalPages <= 1) return null;
    const buttons: number[] = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(data.totalPages, page + 2);
    for (let i = start; i <= end; i++) buttons.push(i);
    if (start > 1) { buttons.unshift(1); if (start > 2) buttons.splice(1, 0, -1); }
    if (end < data.totalPages) { if (end < data.totalPages - 1) buttons.push(-1); buttons.push(data.totalPages); }

    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        {buttons.map((p, i) =>
          p === -1 ? (
            <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-foreground-400 text-xs">...</span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                p === page ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => setPage(Math.min(data.totalPages, page + 1))}
          disabled={page === data.totalPages}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
            Logs do Sistema
          </h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Histórico completo de todas as ações realizadas na plataforma.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-foreground-500">
            {data ? `${data.total} registro(s)` : '—'}
          </span>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-40"
          >
            <i className={`ri-refresh-line text-base ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          {/* Action filter */}
          <div className="relative">
            <select
              value={filterAction}
              onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
              className="px-3 py-2 pr-8 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-700 dark:text-foreground-300 outline-none focus:border-primary-400 cursor-pointer appearance-none"
            >
              <option value="">Todas as ações</option>
              {(data?.actions || []).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-sm" />
          </div>

          {/* User search */}
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={selectedUserName || userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setSelectedUserName('');
                setFilterUserId('');
                setShowUserDropdown(true);
              }}
              onFocus={() => setShowUserDropdown(true)}
              onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
              placeholder="Buscar por usuário..."
              className="w-full pl-9 pr-4 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all"
            />
            {showUserDropdown && userSearch.length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg shadow-lg z-30 max-h-[200px] overflow-y-auto">
                {searchingUsers ? (
                  <div className="px-3 py-2 text-xs text-foreground-400">Buscando...</div>
                ) : userOptions.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-foreground-400">Nenhum usuário encontrado</div>
                ) : (
                  userOptions.map((u) => (
                    <button
                      key={u.id}
                      onMouseDown={() => {
                        setFilterUserId(u.id);
                        setSelectedUserName(u.full_name);
                        setUserSearch('');
                        setShowUserDropdown(false);
                        setPage(1);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <span className="font-medium">{u.full_name}</span>
                      {u.email && <span className="text-foreground-400 text-xs ml-2">{u.email}</span>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Date from */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-500">De:</span>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }}
              className="px-2.5 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-700 dark:text-foreground-300 outline-none focus:border-primary-400 cursor-pointer"
            />
          </div>

          {/* Date to */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-500">Até:</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }}
              className="px-2.5 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-700 dark:text-foreground-300 outline-none focus:border-primary-400 cursor-pointer"
            />
          </div>

          {/* Clear filters */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-close-line text-sm" />
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-foreground-500">Carregando logs...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 dark:border-background-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Ação</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Usuário</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">Detalhes</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">IP</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Data/Hora</th>
                </tr>
              </thead>
              <tbody>
                {(data?.logs || []).map((log) => (
                  <tr key={log.id} className="border-b border-background-50 dark:border-background-200 hover:bg-background-50/60 dark:hover:bg-background-200/50 transition-colors">
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${getActionStyle(log.action)}`}>
                        <i className={`${getActionIcon(log.action)} text-xs`} />
                        {log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="font-medium text-foreground-700 dark:text-foreground-300 text-[13px]">
                        {log.user_name}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <span className="text-foreground-500 dark:text-foreground-400 text-[13px] max-w-[320px] truncate block">
                        {log.details && typeof log.details === 'object'
                          ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                          : String(log.details || '—')}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden lg:table-cell">
                      <code className="text-xs text-foreground-400 bg-background-50 dark:bg-background-200 px-1.5 py-0.5 rounded font-mono">
                        {log.ip_address || '—'}
                      </code>
                    </td>
                    <td className="px-5 py-3 text-right text-foreground-500 dark:text-foreground-400 text-[13px] whitespace-nowrap">
                      {formatDateTime(log.created_at)}
                    </td>
                  </tr>
                ))}
                {(data?.logs || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <i className="ri-file-list-3-line text-4xl text-foreground-300 mb-3 block" />
                      <p className="text-sm text-foreground-500">Nenhum log encontrado</p>
                      <p className="text-xs text-foreground-400 mt-1">
                        {hasFilters ? 'Tente ajustar os filtros de busca' : 'As atividades do sistema aparecerão aqui'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-background-100 dark:border-background-200">
            <span className="text-xs text-foreground-500">
              Mostrando {(page - 1) * perPage + 1}–{Math.min(page * perPage, data.total)} de {data.total}
            </span>
            {renderPageButtons()}
          </div>
        )}
      </div>
    </div>
  );
}